'use client';

import { useBunja } from 'bunja/react';
import {
  ComponentProps,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
} from 'react';

import { overlayBunja } from '@flipster/overlay';
import deferred, { Deferred } from '@flipster/utils/deferred';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { generateID } from '@flipster/utils/identity';

import { Dialog } from './';

type DialogButtonElement = ReactElement<ComponentProps<typeof Dialog.Button>>;

interface SimpleDialogOptions {
  title: { label: ReactNode; leftAddon?: ReactNode };
  content: ReactNode;
  buttons: {
    direction?: 'horizontal' | 'vertical';
    primary: DialogButtonElement;
    secondary?: DialogButtonElement;
  };
}

// 함수형 옵션: SimpleDialogOptions 객체를 반환
type FunctionDialogOptions<T = unknown> = (actions: Deferred<T>) => SimpleDialogOptions;
// 커스텀 옵션: ReactElement를 반환
type CustomDialogOptions<T = unknown> = (actions: Deferred<T>) => ReactElement<ComponentProps<typeof Dialog.Root>>;
// 모든 옵션 타입을 통합
type DialogOpenOptions<T = unknown> = SimpleDialogOptions | FunctionDialogOptions<T> | CustomDialogOptions<T>;

type InternalDialogOptions<T = unknown> = {
  id: string;
  controlledClose: boolean;
  promise: Deferred<T>;
} & (Partial<SimpleDialogOptions> | { dialogComponent: ReactElement<ComponentProps<typeof Dialog.Root>> });

const dialogEmitter = new EventEmitter<{ open: InternalDialogOptions; close: null }>();

export const DialogLayout = ({ children }: PropsWithChildren) => {
  const overlay = useBunja(overlayBunja);

  const handleDialog = useCallback(
    (options: InternalDialogOptions) => {
      const { closed } = overlay.open((close) => {
        options.promise.finally(close);
        if ('dialogComponent' in options) return options.dialogComponent;
        return <DialogComponent {...options} />;
      }, 'alert');

      // ESC나 backdrop 클릭으로 overlay가 닫히면 Promise도 reject 처리
      void closed.then(() => {
        if (options.controlledClose && options.promise.state === 'pending') {
          options.promise.reject(new Error('Dialog closed without action'));
        }
      });
    },
    [overlay],
  );

  const handleClose = useCallback(() => {
    overlay.close('alert');
  }, [overlay]);

  useEffect(() => {
    dialogEmitter.on('open', handleDialog);
    dialogEmitter.on('close', handleClose);
    return () => {
      dialogEmitter.off('open', handleDialog);
      dialogEmitter.off('close', handleClose);
    };
  }, [handleDialog, handleClose]);

  return children;
};

const DialogComponent = (options: Extract<InternalDialogOptions, { title?: unknown }>) => {
  const { title, content, buttons, controlledClose, promise } = options;

  const handleClose = () => {
    if (!controlledClose) {
      promise.resolve(undefined);
    }
  };

  const enhanceButton = (button?: DialogButtonElement) => {
    if (!button) return null;

    return cloneElement(button, {
      ...button.props,
      onClick: (e: MouseEvent<HTMLElement>) => {
        button.props.onClick?.(e as never);
        handleClose();
      },
    });
  };

  return (
    <Dialog.Root>
      {title?.label && <Dialog.Title leftAddon={title.leftAddon} label={title.label} />}
      {content && <Dialog.Content>{content}</Dialog.Content>}
      <Dialog.Footer direction={buttons?.direction}>
        {enhanceButton(buttons?.secondary)}
        {enhanceButton(buttons?.primary)}
      </Dialog.Footer>
    </Dialog.Root>
  );
};

function open<T>(options: SimpleDialogOptions): Promise<T>;
function open<T>(options: FunctionDialogOptions<T>): Promise<T>;
function open<T>(options: CustomDialogOptions<T>): Promise<T>;
function open<T>(options: DialogOpenOptions<T>): Promise<T> {
  const id = generateID('dialog-');
  const promise = deferred<T>();

  if (typeof options === 'function') {
    const result = options(promise);
    if (isValidElement(result)) {
      dialogEmitter.emit('open', {
        id,
        promise,
        controlledClose: true,
        dialogComponent: result,
      });
    } else {
      dialogEmitter.emit('open', {
        id,
        promise,
        controlledClose: true,
        ...result,
      });
    }
  } else {
    dialogEmitter.emit('open', {
      id,
      promise,
      controlledClose: false,
      ...options,
    });
  }

  return promise;
}

export const dialog = {
  open,
  close: () => {
    dialogEmitter.emit('close', null);
  },
};
